import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Skeleton,
  Snackbar,
  Typography,
} from '@mui/material'
import { Download as DownloadIcon } from '@mui/icons-material'
import dayjs from 'dayjs'
import { ReactElement, useState } from 'react'
import { descend, sortWith } from 'ramda'
import axios, { pluckData } from 'src/utilities/http-client'
import { downloadFile } from 'src/v3/utilities/filesystem'
import type { IBSCCertificate, IBSCProgressData } from '../ibsc.slice'

let extensions: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpeg',
  'image/png': 'png',
}

let getCertificationUrl = (id: string, programType: 'ibsc40' | 'ibsc100') =>
  axios
    .get<any>(`/api/v2/profile/${programType}/certificate/export?id=${id}`)
    .then(pluckData)

type Props = {
  data?: IBSCProgressData
  isLoading: boolean
}

type CertRowProps = {
  cert: IBSCCertificate
  downloading: string
  onDownload: (cert: IBSCCertificate) => void
}

let CertRow = (props: CertRowProps): ReactElement => {
  let { cert, downloading, onDownload } = props
  let isDownloading = downloading === cert.id

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 1.5,
        flexWrap: 'wrap',
      }}
    >
      <Chip
        label={cert.programType === 'ibsc40' ? 'IBSC 40' : 'IBSC 100'}
        size="small"
        color={cert.programType === 'ibsc40' ? 'success' : 'warning'}
        sx={{
          minWidth: 72,
        }}
      />
      <Typography
        color="text.secondary"
        variant="body2"
        sx={{
          flex: 1,
        }}
      >
        Earned {dayjs(cert.createdTime).format('MM/DD/YYYY')}
      </Typography>
      {isDownloading ? (
        <CircularProgress size={20} />
      ) : (
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={() => onDownload(cert)}
        >
          Download
        </Button>
      )}
    </Box>
  )
}

export default (props: Props): ReactElement => {
  let { data, isLoading } = props
  let [downloading, setDownloading] = useState<string>('')
  let [errorMessage, setErrorMessage] = useState<string>('')

  let allCerts: IBSCCertificate[] = data
    ? sortWith(
        [
          descend((cert: IBSCCertificate) =>
            new Date(cert.createdTime).getTime(),
          ),
        ],
        [...data.ibsc40.certificates, ...data.ibsc100.certificates],
      )
    : []

  let handleDownload = (cert: IBSCCertificate) => {
    setDownloading(cert.id)
    let programLabel = cert.programType === 'ibsc40' ? '40' : '100'
    getCertificationUrl(cert.id, cert.programType)
      .then((response) =>
        axios.get(response, { responseType: 'blob' }).catch(() => {
          // eslint-disable-next-line fp/no-throw, no-throw-literal
          throw 'File Not Found'
        }),
      )
      .then(pluckData)
      .then((response) =>
        downloadFile(
          response,
          `IBSC-${programLabel}.${extensions[response.type]}`,
        ),
      )
      .then(() => setDownloading(''))
      .catch((err) => {
        setDownloading('')
        setErrorMessage(
          `IBSC ${programLabel} Certificate Download Failed: ${err.response?.data || err}`,
        )
      })
  }

  return (
    <>
      <Card sx={{ p: 3, backgroundColor: 'background.paper', mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          IBSC Certificates
        </Typography>

        {isLoading ? (
          <Box>
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                sx={{
                  height: 44,
                  borderRadius: 1,
                  mb: 1,
                }}
              />
            ))}
          </Box>
        ) : allCerts.length > 0 ? (
          <Box>
            {allCerts.map((cert, index) => (
              <Box key={cert.id}>
                <CertRow
                  cert={cert}
                  downloading={downloading}
                  onDownload={handleDownload}
                />
                {index < allCerts.length - 1 && <Divider />}
              </Box>
            ))}
          </Box>
        ) : (
          <Typography
            color="text.secondary"
            variant="body2"
            sx={{
              fontStyle: 'italic',
            }}
          >
            No IBSC certificates earned yet.
          </Typography>
        )}
      </Card>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
